// ============================================================
// Motor de cálculo de Riesgo Psicosocial — puntaje, clasificación,
// agregación y ranking
// (Ministerio del Trabajo del Ecuador, Guía octubre 2018)
//
// CITA REAL (fuente): la suma simple de las respuestas de cada ítem (sin
// invertir ninguno) y la escala de 4 puntos vienen de la guía del MDT; los
// rangos de clasificación bajo/medio/alto (Tablas 1 a 4) se leen desde
// PSICOSOCIAL_INSTRUMENT (psicosocial-instrumento.ts), no se repiten aquí.
// La guía indica que el cuestionario se aplica a todo el personal y que las
// respuestas deben estar completas (sin ítems sin responder) — de ahí que
// validateAnswers() exija exactamente el conjunto completo de ítems, sin
// faltantes. El ejemplo de interpretación de resultados de la guía
// (sección 11.2) muestra una dimensión con 43% de las personas en bajo,
// 14% en medio y 43% en alto — se usa como caso de verificación de
// aggregateResults() (parafraseado, no citado textualmente).
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de validar
// con Kevin):
//   - PSICOSOCIAL_MIN_GROUP_SIZE = 3 (validado con Kevin 24-25/09/2026):
//     tamaño mínimo de grupo para publicar porcentajes agregados sin
//     suprimir por riesgo de re-identificación. La guía no define este umbral.
//   - El criterio de priorización de rankDimensionsByHighRisk() (ordenar
//     por % en riesgo alto, desempate por % en medio, y como último
//     desempate el orden en que la dimensión aparece en el instrumento) es
//     decisión propia — la guía no define cómo priorizar dimensiones entre
//     sí para intervención.
//   - Los porcentajes de aggregateResults() se calculan sin redondear
//     internamente; el redondeo (a 0 o 2 decimales, según el consumidor)
//     se hace al mostrar, no aquí, para no acumular error de redondeo en
//     cálculos posteriores.
// ============================================================

import { InstrumentDef, RiskLevel, RiskCutoffs, PSICOSOCIAL_INSTRUMENT } from './psicosocial-instrumento';

export type Answers = Record<number, number>;

export type ValidationResult = { ok: true } | { ok: false; errors: string[] };

export interface ScoreResult {
  score: number;
  level: RiskLevel | null;
}

export interface ScoredQuestionnaire {
  dimensions: Record<string, ScoreResult>;
  subdimensions: Record<string, ScoreResult>;
  global: ScoreResult;
}

export type ScoreQuestionnaireResult =
  | { ok: true; questionnaire: ScoredQuestionnaire }
  | { ok: false; errors: string[] };

export interface LevelCounts {
  bajo: number;
  medio: number;
  alto: number;
  n: number;
}

export interface LevelDistribution extends LevelCounts {
  pctBajo: number;
  pctMedio: number;
  pctAlto: number;
}

export type AggregateEntry = LevelDistribution | { suppressed: true; n: number };

export interface AggregateResult {
  dimensions: Record<string, AggregateEntry>;
  subdimensions: Record<string, AggregateEntry>;
  global: AggregateEntry;
}

export type ParticipationResult =
  | {
      ok: true;
      respondents: number;
      headcount: number;
      pct: number;
      overHeadcount: boolean;
      excess: number;
      evaluated: number;
    }
  | { ok: false; errors: string[] };

export interface DimensionRankEntry {
  key: string;
  label: string;
  pctAlto: number;
  pctMedio: number;
  order: number;
}

export interface RankResult {
  ranked: DimensionRankEntry[];
  suppressed: { key: string; label: string; n: number }[];
}

// Validado con Kevin (ver cabecera). CONTRATO: debe coincidir con
// public.psychosocial_min_group_size() de la migración
// 20260925000000_psicosocial_parametros_kevin.sql (psicosocial-resultados-sql.test.ts).
export const PSICOSOCIAL_MIN_GROUP_SIZE = 3;

// ===== 1. Validación de respuestas =====

// La guía exige responder todos los ítems del cuestionario (sin ítems sin
// contestar) — validateAnswers() exige exactamente los ítems
// 1..instrument.itemCount, cada uno con un valor entero de 1 a 4. Devuelve
// la lista completa de errores encontrados, no solo el primero, para que
// el formulario pueda señalar todos los campos con problema de una vez.
export function validateAnswers(
  answers: Answers,
  instrument: InstrumentDef = PSICOSOCIAL_INSTRUMENT
): ValidationResult {
  const errors: string[] = [];
  const answerKeys = Object.keys(answers).map(Number);
  const answerKeySet = new Set(answerKeys);

  for (let item = 1; item <= instrument.itemCount; item++) {
    if (!answerKeySet.has(item)) {
      errors.push(`Falta la respuesta al ítem ${item}`);
    }
  }

  for (const key of answerKeys) {
    if (!Number.isInteger(key) || key < 1 || key > instrument.itemCount) {
      errors.push(`El ítem ${key} no pertenece al instrumento (rango válido: 1-${instrument.itemCount})`);
    }
  }

  for (const key of answerKeys) {
    if (!Number.isInteger(key) || key < 1 || key > instrument.itemCount) continue; // ya reportado arriba
    const value = (answers as Record<number, unknown>)[key];
    const isValid = typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 4;
    if (!isValid) {
      errors.push(`Respuesta inválida en el ítem ${key}: ${JSON.stringify(value)} (debe ser un entero de 1 a 4)`);
    }
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

// ===== 2. Clasificación por rango =====

// Clasifica un puntaje contra los 3 rangos de un RiskCutoffs. Devuelve null
// si el puntaje no cae en ninguno de los 3 — no debería ocurrir para un
// cuestionario validado con validateAnswers() (los rangos del instrumento
// cubren exactamente [n, 4n] sin huecos, verificado en psicosocial-
// instrumento.ts), pero se maneja explícitamente por seguridad ante datos
// que no pasaron por validateAnswers().
export function classifyScore(score: number, cutoffs: RiskCutoffs): RiskLevel | null {
  if (score >= cutoffs.alto.min && score <= cutoffs.alto.max) return 'alto';
  if (score >= cutoffs.medio.min && score <= cutoffs.medio.max) return 'medio';
  if (score >= cutoffs.bajo.min && score <= cutoffs.bajo.max) return 'bajo';
  return null;
}

// ===== 3. Puntaje de un cuestionario =====

// Valida primero; si hay errores, no calcula nada y los devuelve todos. Si
// es válido, suma simple (sin inversión de ningún ítem — ver DECISIÓN
// PROPIA en la cabecera) por dimensión, por subdimensión y el global (suma
// de las 8 dimensiones). Verifica internamente que la suma de las
// subdimensiones de "otros_puntos_importantes" coincida con el puntaje de
// esa dimensión — protege contra un futuro cambio que desincronice los
// ítems de una subdimensión respecto a los de su dimensión padre.
export function scoreQuestionnaire(
  answers: Answers,
  instrument: InstrumentDef = PSICOSOCIAL_INSTRUMENT
): ScoreQuestionnaireResult {
  const validation = validateAnswers(answers, instrument);
  if (!validation.ok) return { ok: false, errors: validation.errors };

  const dimensions: Record<string, ScoreResult> = {};
  const subdimensions: Record<string, ScoreResult> = {};
  const errors: string[] = [];

  for (const dim of instrument.dimensions) {
    const dimScore = dim.items.reduce((acc, item) => acc + answers[item], 0);
    dimensions[dim.key] = { score: dimScore, level: classifyScore(dimScore, dim.cutoffs) };

    if (dim.subdimensions) {
      let subSum = 0;
      for (const sub of dim.subdimensions) {
        const subScore = sub.items.reduce((acc, item) => acc + answers[item], 0);
        subdimensions[sub.key] = { score: subScore, level: classifyScore(subScore, sub.cutoffs) };
        subSum += subScore;
      }
      if (subSum !== dimScore) {
        errors.push(
          `Inconsistencia interna: la suma de las subdimensiones de "${dim.key}" (${subSum}) no coincide con su puntaje (${dimScore})`
        );
      }
    }
  }

  const globalScore = instrument.dimensions.reduce((acc, dim) => acc + dimensions[dim.key].score, 0);
  const global: ScoreResult = { score: globalScore, level: classifyScore(globalScore, instrument.globalCutoffs) };

  if (errors.length > 0) return { ok: false, errors };

  return { ok: true, questionnaire: { dimensions, subdimensions, global } };
}

// ===== 4. Agregación de varios cuestionarios =====

function tallyLevels(results: ScoreResult[]): LevelCounts {
  const counts: LevelCounts = { bajo: 0, medio: 0, alto: 0, n: results.length };
  for (const r of results) {
    if (r.level === 'bajo') counts.bajo++;
    else if (r.level === 'medio') counts.medio++;
    else if (r.level === 'alto') counts.alto++;
  }
  return counts;
}

// Porcentajes sin redondear — ver DECISIÓN PROPIA en la cabecera.
function toDistribution(counts: LevelCounts): LevelDistribution {
  const { n } = counts;
  return {
    ...counts,
    pctBajo: n > 0 ? (counts.bajo / n) * 100 : 0,
    pctMedio: n > 0 ? (counts.medio / n) * 100 : 0,
    pctAlto: n > 0 ? (counts.alto / n) * 100 : 0,
  };
}

// Agrega una lista de cuestionarios ya puntuados. Si la lista es más chica
// que minGroupSize (PSICOSOCIAL_MIN_GROUP_SIZE por defecto — DECISIÓN
// PROPIA, ver cabecera), no calcula conteos ni porcentajes: devuelve
// `{ suppressed: true, n }` para evitar publicar un dato agregado que en
// grupos muy chicos equivale a re-identificar a una persona.
export function aggregateResults(
  list: ScoredQuestionnaire[],
  options: { minGroupSize?: number } = {},
  instrument: InstrumentDef = PSICOSOCIAL_INSTRUMENT
): AggregateResult {
  // El umbral solo se puede subir por parámetro (options.minGroupSize); no
  // se puede bajar por debajo de PSICOSOCIAL_MIN_GROUP_SIZE desde afuera —
  // cambiar el piso implica cambiar la constante misma (y su espejo SQL),
  // no pasar un valor más chico aquí.
  const minGroupSize = Math.max(options.minGroupSize ?? PSICOSOCIAL_MIN_GROUP_SIZE, PSICOSOCIAL_MIN_GROUP_SIZE);
  const n = list.length;
  const suppressed = n < minGroupSize;

  const dimensions: Record<string, AggregateEntry> = {};
  const subdimensions: Record<string, AggregateEntry> = {};

  for (const dim of instrument.dimensions) {
    dimensions[dim.key] = suppressed
      ? { suppressed: true, n }
      : toDistribution(tallyLevels(list.map((q) => q.dimensions[dim.key])));

    if (dim.subdimensions) {
      for (const sub of dim.subdimensions) {
        subdimensions[sub.key] = suppressed
          ? { suppressed: true, n }
          : toDistribution(tallyLevels(list.map((q) => q.subdimensions[sub.key])));
      }
    }
  }

  const global: AggregateEntry = suppressed
    ? { suppressed: true, n }
    : toDistribution(tallyLevels(list.map((q) => q.global)));

  return { dimensions, subdimensions, global };
}

// ===== 4a. Repuestos de códigos de acceso =====

// CITA REAL: ninguna norma fija un margen de códigos de repuesto.
// DECISIÓN validada con Kevin (24-25/09/2026): 15 % de repuestos sobre el
// total de trabajadores, redondeado hacia arriba; el redondeo
// hacia arriba ya garantiza al menos 1 repuesto para cualquier
// headcount >= 1.
// CONTRATO: este cálculo debe coincidir exactamente con la función SQL
// psychosocial_max_codes de la migración
// 20260925000000_psicosocial_parametros_kevin.sql — una prueba de
// deriva lo vigila. La participación (computeParticipation, más abajo)
// se calcula sobre el total de trabajadores, no sobre la cantidad de
// códigos generados — los repuestos no inflan esa base.
export const PSICOSOCIAL_SPARE_CODES_PCT = 15;

export function maxCodesForHeadcount(headcount: number): number {
  if (!Number.isInteger(headcount) || headcount < 1) {
    throw new Error(`maxCodesForHeadcount: headcount inválido: ${headcount} (debe ser un entero mayor o igual a 1)`);
  }
  // Misma aritmética entera que el SQL: ceil(h * pct / 100) = floor((h * pct + 99) / 100).
  return headcount + Math.floor((headcount * PSICOSOCIAL_SPARE_CODES_PCT + 99) / 100);
}

// ===== 5. Participación (actividad 3 del programa) =====

// La guía exige registrar la participación (respondents/headcount) como
// parte del programa de gestión del riesgo psicosocial. headcount debe ser
// mayor a 0. Con códigos de repuesto, respondents puede superar a headcount
// (por ejemplo, dos personas usando dos códigos distintos para el mismo
// puesto) — eso NO es un error de entrada, sino una señal a mostrar al
// usuario (posible doble respuesta): pct se topa en 100 y overHeadcount
// queda en true en vez de devolver un error o un porcentaje > 100%.
// `evaluated` es el valor que va al numerador de la actividad 3 del
// programa, porque la base exige que el numerador no supere al denominador.
export function computeParticipation(respondents: number, headcount: number): ParticipationResult {
  const errors: string[] = [];
  if (!Number.isInteger(headcount) || headcount < 1) {
    errors.push(`headcount inválido: ${headcount} (debe ser un entero mayor o igual a 1)`);
  }
  if (!Number.isInteger(respondents) || respondents < 0) {
    errors.push(`respondents inválido: ${respondents} (debe ser un entero mayor o igual a 0)`);
  }

  if (errors.length > 0) return { ok: false, errors };

  const overHeadcount = respondents > headcount;
  const excess = Math.max(0, respondents - headcount);
  const evaluated = Math.min(respondents, headcount);
  const pct = Math.min((respondents / headcount) * 100, 100);

  return { ok: true, respondents, headcount, pct, overHeadcount, excess, evaluated };
}

// ===== 6. Ranking de dimensiones por riesgo alto =====

// Ordena las dimensiones no suprimidas por % en riesgo alto (desc);
// desempate por % en medio (desc); desempate final por el orden en que la
// dimensión aparece en el instrumento. Criterio de priorización: DECISIÓN
// PROPIA, ver cabecera — la guía no define cómo priorizar. Las dimensiones
// suprimidas (grupo < minGroupSize en aggregateResults) no se incluyen en
// el ranking — se listan aparte en `suppressed`.
export function rankDimensionsByHighRisk(
  aggregate: AggregateResult,
  instrument: InstrumentDef = PSICOSOCIAL_INSTRUMENT
): RankResult {
  const ranked: DimensionRankEntry[] = [];
  const suppressed: { key: string; label: string; n: number }[] = [];

  instrument.dimensions.forEach((dim, order) => {
    const entry = aggregate.dimensions[dim.key];
    if ('suppressed' in entry && entry.suppressed) {
      suppressed.push({ key: dim.key, label: dim.label, n: entry.n });
    } else {
      const dist = entry as LevelDistribution;
      ranked.push({ key: dim.key, label: dim.label, pctAlto: dist.pctAlto, pctMedio: dist.pctMedio, order });
    }
  });

  ranked.sort((a, b) => {
    if (b.pctAlto !== a.pctAlto) return b.pctAlto - a.pctAlto;
    if (b.pctMedio !== a.pctMedio) return b.pctMedio - a.pctMedio;
    return a.order - b.order;
  });

  return { ranked, suppressed };
}
